import { lazy, Suspense, useCallback, useRef, useState } from "react";

import { FileAttachmentIcon } from "@vericus/cadmus-icons";
import { Button, Divider, Spacer, Text } from "@vericus/cadmus-ui";

import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";

import { BlockAttachmentFragment } from "@/generated/graphql";
import {
  useRemoveBlockAttachment,
  useUploadBlockAttachment,
} from "@/hooks/use-block-attachment";

import { AttachmentUploadControls } from "./attachment-upload-controls";
import { UploadedAttachmentsList } from "./uploaded-attachments-list";
import { UploadingFile, UploadingFilesList } from "./uploading-files-list";
import * as styles from "./work-attachment-section.css";
import { WorkAttachmentsModal } from "./work-attachments-modal";

/**
 * Lazy-loaded webcam capture modal.
 * This heavy component (500+ lines with MediaRecorder API, canvas operations, etc.)
 * is only loaded when the user clicks the camera button, reducing initial bundle size.
 */
const WebcamCaptureModal = lazy(() =>
  import("@/components/webcam-capture-modal/webcam-capture-modal").then(
    (module) => ({
      default: module.WebcamCaptureModal,
    })
  )
);

/**
 * Display mode for the work attachment section
 * - 'classic': Shows empty state when no files are present
 * - 'minimal': Hides empty state
 */
type WorkAttachmentMode = "classic" | "minimal";

/**
 * Props for the WorkAttachmentSection component
 */
interface WorkAttachmentSectionProps {
  /** Comma-separated list of accepted file types (e.g., ".pdf,.doc,.jpg") */
  acceptedFileTypes?: string;
  /** Maximum file size in bytes (default: 200MB) */
  maxFileSize?: number;
  /** Whether to allow multiple file uploads */
  multiple?: boolean;
  /** Whether the upload controls should be disabled */
  disabled?: boolean;
  /** When true (LDB exam mode), disables file upload but allows camera capture */
  ldbExamMode?: boolean;
  /** Answer block ID to associate attachments with */
  answerBlockId: string;
  /** Display mode for the component */
  mode: WorkAttachmentMode;
  /** Attachments */
  attachments: BlockAttachmentFragment[];
}

const DOCUMENT_TYPES = ".pdf,.doc,.docx,.rtf,.txt";
const SPREADSHEET_TYPES = ".csv,.xlsx,.xls,.pbix";
const PRESENTATION_TYPES = ".ppt,.pptx";
const IMAGE_TYPES = ".jpg,.jpeg,.png,.bmp,.tiff";
const AUDIO_TYPES = ".mp3,.wav,.m4a";
const VIDEO_TYPES = ".mp4,.mov,.avi";
const ARCHIVE_TYPES = ".zip";
const ENGINEERING_TYPES = ".xml,.xer,.cad,.mpp";

const DEFAULT_ACCEPTED_FILE_TYPES = [
  DOCUMENT_TYPES,
  SPREADSHEET_TYPES,
  PRESENTATION_TYPES,
  IMAGE_TYPES,
  AUDIO_TYPES,
  VIDEO_TYPES,
  ARCHIVE_TYPES,
  ENGINEERING_TYPES,
].join(",");
const DEFAULT_MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB default

/**
 * WorkAttachmentSection component for student work attachments.
 * Supports file uploads via traditional file picker and webcam capture.
 * Displays uploading progress, uploaded files, and provides file management capabilities.
 *
 * Features:
 * - File upload with validation (size, type)
 * - Webcam capture support
 * - Upload progress tracking
 * - File deletion
 * - LDB exam mode (camera-only)
 * - MFA answer block association
 */
export function WorkAttachmentSection({
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  multiple = true,
  disabled = false,
  ldbExamMode = false,
  answerBlockId,
  mode,
  attachments,
}: WorkAttachmentSectionProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isCaptureModalOpen, setIsCaptureModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadBlockAttachment] = useUploadBlockAttachment(answerBlockId);
  const [deleteBlockAttachment, isDeleting] = useRemoveBlockAttachment();

  /**
   * Validates a file against size constraints
   * @param file - The file to validate
   * @returns Error message if validation fails, null otherwise
   */
  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxFileSize) {
        return `File "${file.name}" is too large. Maximum size is ${Math.round(maxFileSize / 1024 / 1024)}MB`;
      }
      return null;
    },
    [maxFileSize]
  );

  /**
   * Sets the real upload progress for a file by its temporary ID
   */
  const updateUploadProgress = useCallback((tempId: string, pct: number) => {
    setUploadingFiles((prev) =>
      prev.map((f) => (f.tempId === tempId ? { ...f, progress: pct } : f))
    );
  }, []);

  /**
   * Generates a unique temporary ID for tracking file uploads
   */
  const generateTempId = useCallback(() => {
    return `temp-${Date.now()}-${Math.random()}`;
  }, []);

  /**
   * Updates uploading file progress to completion
   */
  const completeUploadProgress = useCallback((tempId: string) => {
    setUploadingFiles((prev) =>
      prev.map((f) => (f.tempId === tempId ? { ...f, progress: 100 } : f))
    );
  }, []);

  /**
   * Removes a file from the uploading list
   */
  const removeFromUploadingList = useCallback((tempId: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.tempId !== tempId));
  }, []);

  /**
   * Sets error state on an uploading file
   */
  const setUploadError = useCallback((tempId: string, errorMessage: string) => {
    setUploadingFiles((prev) =>
      prev.map((f) =>
        f.tempId === tempId ? { ...f, hasError: true, errorMessage } : f
      )
    );
  }, []);

  /**
   * Uploads a single file with progress tracking
   */
  const uploadSingleFile = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const tempId = generateTempId();

      // Add to uploading files list
      setUploadingFiles((prev) => [
        ...prev,
        { tempId, name: file.name, progress: 0, file },
      ]);

      try {
        const result = await uploadBlockAttachment(file.name, file, (pct) =>
          updateUploadProgress(tempId, pct)
        );

        if (result) {
          completeUploadProgress(tempId);

          // Wait briefly to show completion, then remove from uploading list
          // The attachment is automatically added to cache by the mutation
          setTimeout(() => {
            removeFromUploadingList(tempId);
          }, 500);
        } else {
          // Upload returned no result - treat as error
          setUploadError(tempId, "Upload failed - no response from server");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setUploadError(tempId, errorMessage);
      }
    },
    [
      validateFile,
      generateTempId,
      uploadBlockAttachment,
      updateUploadProgress,
      completeUploadProgress,
      removeFromUploadingList,
      setUploadError,
    ]
  );

  /**
   * Handles file selection from the file input
   * Validates, uploads, and tracks progress for each selected file
   */
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      const fileArray = Array.from(files);

      // Upload files sequentially to avoid overwhelming the server
      for (const file of fileArray) {
        await uploadSingleFile(file);
      }

      // Clear input for re-selection of same files
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [uploadSingleFile]
  );

  /**
   * Triggers the hidden file input when upload button is clicked
   */
  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Handles file deletion
   * The cache is automatically updated by the mutation
   * @param fileId - ID of the file to delete
   */
  const handleRemoveFile = useCallback(
    async (fileId: string) => {
      await deleteBlockAttachment(fileId);
    },
    [deleteBlockAttachment]
  );

  /**
   * Handles file capture from webcam
   * Reuses the same upload logic as file selection
   * @param file - The captured file
   */
  const handleCapture = useCallback(
    async (file: File) => {
      await uploadSingleFile(file);
    },
    [uploadSingleFile]
  );

  /**
   * Handles retry of a failed upload
   * @param tempId - Temporary ID of the file to retry
   */
  const handleRetryUpload = useCallback(
    async (tempId: string) => {
      const fileToRetry = uploadingFiles.find((f) => f.tempId === tempId);
      if (!fileToRetry) return;

      // Reset error state and progress
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.tempId === tempId
            ? { ...f, hasError: false, errorMessage: undefined, progress: 0 }
            : f
        )
      );

      // Retry the upload
      try {
        const result = await uploadBlockAttachment(
          fileToRetry.name,
          fileToRetry.file,
          (pct) => updateUploadProgress(tempId, pct)
        );

        if (result) {
          completeUploadProgress(tempId);

          // Wait briefly to show completion, then remove from uploading list
          // The attachment is automatically added to cache by the mutation
          setTimeout(() => {
            removeFromUploadingList(tempId);
          }, 500);
        } else {
          // Upload returned no result - treat as error
          setUploadError(tempId, "Upload failed - no response from server");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setUploadError(tempId, errorMessage);
      }
    },
    [
      uploadingFiles,
      uploadBlockAttachment,
      updateUploadProgress,
      completeUploadProgress,
      removeFromUploadingList,
      setUploadError,
    ]
  );

  /**
   * Handles dismissing a failed upload
   * @param tempId - Temporary ID of the file to dismiss
   */
  const handleDismissError = useCallback((tempId: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.tempId !== tempId));
  }, []);

  const isUploading = uploadingFiles.length > 0;

  const showEmptyState =
    attachments.length === 0 &&
    uploadingFiles.length === 0 &&
    mode === "classic";

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        multiple={multiple}
        onChange={handleFileSelect}
        style={{ display: "none" }}
        disabled={disabled || isUploading}
        aria-label="File upload input"
      />

      {mode === "classic" && (
        <>
          <Text kind="displayThree">Files</Text>
          <Text kind="bodyLine">Attach files from device or camera</Text>
          <Spacer spacing={20} />
          <Divider />
          <Spacer spacing={20} />
        </>
      )}

      {/* Upload controls (camera and upload buttons) */}
      <AttachmentUploadControls
        onCameraClick={() => setIsCaptureModalOpen(true)}
        onUploadClick={handleButtonClick}
        maxFileSize={maxFileSize}
        disabled={disabled || !answerBlockId}
        isUploading={isUploading}
        isDeleting={isDeleting}
        ldbExamMode={ldbExamMode}
      />

      {/* Files currently being uploaded */}
      <UploadingFilesList
        files={uploadingFiles}
        onRetry={handleRetryUpload}
        onDismiss={handleDismissError}
      />

      {/* Successfully uploaded attachments */}
      <UploadedAttachmentsList
        attachments={attachments}
        onDelete={handleRemoveFile}
        onShowAll={() => setIsModalOpen(true)}
        previewLimit={2}
        displayMode={mode === "classic" ? "full" : "compact"}
      />

      {/* Empty state when no attachments */}
      {showEmptyState && <EmptyFilesState />}

      {/* Lazy-loaded webcam modal - only renders when opened */}
      {isCaptureModalOpen && (
        <Sentry.ErrorBoundary
          fallback={
            <CameraLoadError
              onRetry={() => {
                setIsCaptureModalOpen(false);
                // Allow a brief moment for state to reset before reopening
                setTimeout(() => setIsCaptureModalOpen(true), 500);
              }}
            />
          }
        >
          <Suspense
            fallback={
              <div className={styles.loadingOverlay}>
                <div className={styles.loadingCard}>
                  <Text kind="bodyMd">Loading camera...</Text>
                </div>
              </div>
            }
          >
            <WebcamCaptureModal
              onClose={() => setIsCaptureModalOpen(false)}
              onCapture={handleCapture}
              maxFileSize={maxFileSize}
            />
          </Suspense>
        </Sentry.ErrorBoundary>
      )}

      {/* All Files Modal */}
      <WorkAttachmentsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        answerBlockId={answerBlockId}
        attachments={attachments}
      />
    </>
  );
}

/**
 * Props for the EmptyFilesState component
 */
interface EmptyFilesStateProps {
  /** Primary message to display */
  primaryMessage?: string;
  /** Secondary message to display */
  secondaryMessage?: string;
  /** Emoji or icon to display (default: folder emoji) */
  icon?: string;
}

/**
 * Displays an empty state message when no files have been uploaded.
 *
 * @example
 * ```tsx
 * <EmptyFilesState
 *   primaryMessage="No files uploaded yet"
 *   secondaryMessage="Upload files to include them with your submission"
 * />
 * ```
 */
export function EmptyFilesState({
  primaryMessage = "No files uploaded yet",
  secondaryMessage = "Upload files to include them with your submission",
}: EmptyFilesStateProps) {
  return (
    <>
      <Spacer spacing={24} />
      <div className={styles.emptyState}>
        <FileAttachmentIcon label="work attachments" size={48} />
        <Spacer spacing={12} />
        <Text kind="bodyMd" color="grey500">
          {primaryMessage}
        </Text>
        <Text kind="bodyMd" color="grey500">
          {secondaryMessage}
        </Text>
      </div>
    </>
  );
}

/**
 * Error fallback component shown when webcam modal fails to load
 */
function CameraLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className={styles.loadingOverlay}>
      <div className={styles.loadingCard}>
        <Text kind="headingSix" style={{ marginBottom: "12px" }}>
          Failed to load camera
        </Text>
        <Text kind="bodyMd" style={{ marginBottom: "16px", color: "#666" }}>
          There was an error loading the camera interface. Please try again.
        </Text>
        <Button kind="primary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}
